import { zodResolver } from "@hookform/resolvers/zod";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useForm, FormProvider } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/ui/button";
import { useAppDispatch, useAppSelector } from "@/store";

import { AddressFieldsProvider } from "../contexts/address-fields-context";
import { usePaymentMethodForms } from "../contexts/use-payment-method-forms";
import { SchemaGenerator } from "../services/schema-generator";
import {
  clearServerFieldError,
  selectServerFieldErrors,
} from "../store/payment-method-form-slice";
import {
  MethodProviderName,
  type PaymentMethod,
} from "../types/available-payment-methods";

import { EvervaultCard } from "./FormControl/EvervaultCard";
import { FormControlFactory } from "./FormControl/FormControlFactory";

import type {
  ExtendedForm,
  ExtendedProperty,
} from "../hooks/use-available-payment-methods";

interface PaymentMethodFormProps {
  paymentMethod: PaymentMethod;
  form: ExtendedForm;
  onSubmit: (values: Record<string, string | number>) => void;
  asyncValidators?: Record<string, (value: string) => Promise<string | null>>;
}

const DEBIT_CARD_FIELDS = ["card_number", "expiration_date"];

export const PaymentMethodForm: React.FC<PaymentMethodFormProps> = ({
  paymentMethod,
  form,
  onSubmit,
  asyncValidators = {},
}) => {
  const { t } = useTranslation();
  const { formData, updateFormData } = usePaymentMethodForms();
  const [isLoading, setIsLoading] = useState(false);
  const defaultValues = useMemo(
    () =>
      formData[form.name] ||
      form.fields.reduce(
        (acc, field) => {
          acc[field.property_name] = "";
          return acc;
        },
        {} as Record<string, string>
      ),
    [formData, form.fields, form.name]
  );

  const isDebitCard =
    paymentMethod.method_provider_name ===
    MethodProviderName.DEBIT_CARD_CHECKBOOK;

  const debitCardFields = useMemo(() => {
    if (!isDebitCard) {
      return null;
    }

    const fields = form.fields
      .filter((field) => DEBIT_CARD_FIELDS.includes(field.property_name))
      .reduce(
        (acc, field) => {
          acc[field.property_name] = field;
          return acc;
        },
        {} as Record<string, ExtendedProperty>
      );

    return fields.card_number && fields.expiration_date ? fields : null;
  }, [form.fields, isDebitCard]);

  const schema = React.useMemo(() => {
    const formSchema = SchemaGenerator.generateFormSchema(form.fields);

    return debitCardFields
      ? formSchema.extend({
          expiration_date: SchemaGenerator.generateCardExpirySchema(
            debitCardFields.expiration_date
          ),
        })
      : formSchema;
  }, [form.fields, debitCardFields]);

  const methods = useForm({
    resolver: zodResolver(schema),
    mode: "onSubmit",
    reValidateMode: "onChange",
    defaultValues,
  });

  const dispatch = useAppDispatch();
  const serverFieldErrors = useAppSelector(selectServerFieldErrors)[form.name];
  const latestServerFieldErrors = useRef(serverFieldErrors);
  const serverFieldErrorsOnMount = useRef(serverFieldErrors);

  useEffect(() => {
    latestServerFieldErrors.current = serverFieldErrors;
  }, [serverFieldErrors]);

  useEffect(() => {
    const errors = serverFieldErrorsOnMount.current;
    if (!errors) {
      return;
    }

    form.fields
      .filter((field) => errors[field.property_name])
      .forEach((field, index) => {
        methods.setError(
          field.property_name,
          {
            type: "server",
            message:
              errors[field.property_name] ||
              t("invalid_field_error", { 0: field.title }),
          },
          { shouldFocus: index === 0 }
        );
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- apply once per mount
  }, []);

  // `mode: "onSubmit"` means react-hook-form will not clear a mount-time
  // `setError` as the participant types, so we clear it ourselves.
  useEffect(() => {
    const subscription = methods.watch((_values, { name }) => {
      if (!name || !latestServerFieldErrors.current?.[name]) {
        return;
      }

      methods.clearErrors(name);
      dispatch(clearServerFieldError({ formName: form.name, fieldName: name }));
    });

    return () => subscription.unsubscribe();
  }, [methods, dispatch, form.name]);

  const handleSubmit = methods.handleSubmit(async (data) => {
    setIsLoading(true);

    const asyncErrors: Record<string, string> = {};
    for (const [fieldName, validator] of Object.entries(asyncValidators)) {
      const error = await validator(data[fieldName] as string);
      if (error) {
        asyncErrors[fieldName] = error;
      }
    }

    if (Object.keys(asyncErrors).length > 0) {
      Object.entries(asyncErrors).forEach(([field, error]) => {
        methods.setError(field, { message: error });
      });
      setIsLoading(false);
      return;
    }
    setIsLoading(false);
    updateFormData(form.name, data);
    onSubmit(data);
  });

  const formFields = useMemo(
    () =>
      isDebitCard
        ? form.fields.filter(
            (field) => !DEBIT_CARD_FIELDS.includes(field.property_name)
          )
        : form.fields,
    [form.fields, isDebitCard]
  );

  return (
    <FormProvider {...methods}>
      <AddressFieldsProvider form={form}>
        <form
          aria-label={`${form.name} form`}
          name={`${form.name}-form`}
          onSubmit={handleSubmit}
          className="my-2"
        >
          <div className="flex flex-col gap-y-4">
            {formFields.map((field) => (
              <div className="w-full" key={field.property_name}>
                {FormControlFactory.createFormControl({
                  field,
                  name: field.property_name,
                  countryCode: paymentMethod.country,
                })}
              </div>
            ))}

            {debitCardFields && <EvervaultCard fields={debitCardFields} />}

            <Button
              className="w-full mt-4"
              size="lg"
              type="submit"
              loading={isLoading}
            >
              {t("continue_text")}
            </Button>
          </div>
        </form>
      </AddressFieldsProvider>
    </FormProvider>
  );
};
